import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  BanknotesIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  WalletIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { subscriptionApi } from '../../services/subscription.service';
import { PaymentHistory as PaymentHistoryType, SubscriptionProrationPreview } from '../../types/subscription';

interface PaymentState {
  planId: string;
  planName: string;
  amount: number;
  currency?: string;
  billingCycle?: 'monthly' | 'annual' | string;
  mode?: 'new' | 'upgrade';
  originalAmount?: number;
  prorationPreview?: SubscriptionProrationPreview;
}

interface PendingSubscriptionPurchase extends PaymentState {
  role: 'TEACHER' | 'STUDENT';
}

const PENDING_SUBSCRIPTION_KEY = 'pendingSubscriptionPurchase';
const PENDING_TEACHER_PAYMENT_KEY = 'pendingTeacherPlanPayment';

const formatCurrency = (value: number, currency = 'INR') => `${currency} ${Number(value || 0).toFixed(2)}`;

const statusClasses: Record<string, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  failed: 'border-rose-200 bg-rose-50 text-rose-700',
  pending: 'border-amber-200 bg-amber-50 text-amber-700',
};

const TeacherPayments: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as PaymentState | null) || null;

  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [topupAmount, setTopupAmount] = useState('');
  const [history, setHistory] = useState<PaymentHistoryType[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [pendingPaymentState, setPendingPaymentState] = useState<PaymentState | null>(null);
  const [prorationPreview, setProrationPreview] = useState<SubscriptionProrationPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const rawPaymentState = useMemo(() => {
    const effectiveState = state?.planId ? state : pendingPaymentState;
    return effectiveState?.planId ? effectiveState : null;
  }, [pendingPaymentState, state]);

  const paymentState = useMemo(() => {
    const effectiveState = rawPaymentState;
    if (!effectiveState?.planId || !effectiveState?.planName) return null;
    const effectiveProration = prorationPreview || effectiveState.prorationPreview || null;
    return {
      planId: effectiveState.planId,
      planName: effectiveState.planName,
      amount: Number(effectiveProration?.amountDue ?? effectiveState.amount ?? 0),
      originalAmount: Number(effectiveState.originalAmount ?? effectiveState.amount ?? 0),
      currency: effectiveState.currency || 'INR',
      billingCycle: effectiveState.billingCycle || 'monthly',
      mode: effectiveState.mode || 'new',
      prorationPreview: effectiveProration || undefined,
    };
  }, [prorationPreview, rawPaymentState]);

  const loadWalletBalance = async () => {
    try {
      const response = await api.get<{ balance: number }>('/teachers/wallet/balance');
      setWalletBalance(Number(response.data?.balance || 0));
    } catch (error) {
      console.error('Failed to load wallet balance:', error);
    }
  };

  const loadPaymentHistory = async () => {
    try {
      setHistoryLoading(true);
      const data = await subscriptionApi.getPaymentHistory();
      setHistory(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load payment history:', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    void loadWalletBalance();
    void loadPaymentHistory();
  }, []);

  useEffect(() => {
    if (state?.planId) return;
    const stored = localStorage.getItem(PENDING_TEACHER_PAYMENT_KEY);
    if (!stored) return;
    try {
      const parsed = JSON.parse(stored) as PaymentState;
      if (parsed?.planId && parsed?.planName) {
        setPendingPaymentState(parsed);
      }
    } catch (error) {
      console.error('Failed to parse pending teacher payment state:', error);
    }
  }, [state]);

  useEffect(() => {
    if (!rawPaymentState?.planId || rawPaymentState.mode !== 'upgrade') {
      setProrationPreview(null);
      return;
    }
    if (rawPaymentState.prorationPreview) {
      setProrationPreview(rawPaymentState.prorationPreview);
      return;
    }

    let cancelled = false;
    setProrationPreview(null);
    const loadPreview = async () => {
      try {
        setPreviewLoading(true);
        const preview = await subscriptionApi.previewPlanSwitch(rawPaymentState.planId);
        if (!cancelled) {
          setProrationPreview(preview);
        }
      } catch (error) {
        console.error('Failed to calculate prorated teacher amount:', error);
      } finally {
        if (!cancelled) {
          setPreviewLoading(false);
        }
      }
    };

    void loadPreview();
    return () => {
      cancelled = true;
    };
  }, [rawPaymentState?.planId, rawPaymentState?.mode, rawPaymentState?.prorationPreview]);

  const subscribeWithWallet = async () => {
    if (!paymentState) return;

    try {
      setLoading(true);
      await api.post(`/teachers/subscription/subscribe/${paymentState.planId}`, {
        paymentMethodId: 'WALLET',
      });
      localStorage.removeItem(PENDING_TEACHER_PAYMENT_KEY);
      alert('Subscription payment successful.');
      navigate('/teacher/subscription-plans');
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Subscription payment failed.';
      alert(message);
      await loadWalletBalance();
    } finally {
      setLoading(false);
    }
  };

  const continueWithRazorpay = () => {
    const amount = paymentState ? Number(paymentState.amount || 0) : Number(topupAmount || 0);
    if (!Number.isFinite(amount) || amount <= 0) {
      alert('Enter a valid amount.');
      return;
    }

    if (paymentState) {
      const pendingPurchase: PendingSubscriptionPurchase = {
        ...paymentState,
        role: 'TEACHER',
      };
      localStorage.setItem(PENDING_SUBSCRIPTION_KEY, JSON.stringify(pendingPurchase));
    }

    navigate('/student/wallet/topup', { state: { amount, role: 'TEACHER' } });
  };

  const totalPaid = history
    .filter((p) => p.status === 'success')
    .reduce((sum, p) => sum + Number(p.amount || 0), 0);

  const metrics = [
    {
      label: 'Wallet balance',
      value: formatCurrency(walletBalance),
      hint: 'Available for wallet-based subscription checkout',
      icon: WalletIcon,
    },
    {
      label: 'Successful payments',
      value: formatCurrency(totalPaid),
      hint: 'Total captured across recorded payment history',
      icon: BanknotesIcon,
    },
    {
      label: 'Pending subscription',
      value: paymentState ? paymentState.planName : 'None',
      hint: paymentState ? `${paymentState.billingCycle} checkout is ready` : 'No plan selected for checkout',
      icon: CreditCardIcon,
    },
  ];

  return (
    <div className="space-y-6 text-slate-900">
      <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(34,197,94,0.18),_transparent_26%),linear-gradient(135deg,_#020617_0%,_#0f172a_48%,_#0f766e_100%)] px-6 py-8 text-white shadow-[0_30px_90px_-52px_rgba(15,23,42,0.8)] sm:px-8 lg:px-10">
        <div className="grid gap-8 lg:grid-cols-[minmax(0,1.15fr)_320px] lg:items-end">
          <div>
            <span className="inline-flex rounded-full border border-white/15 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-cyan-100">
              Teacher payments
            </span>
            <h1 className="mt-5 text-3xl font-semibold tracking-tight sm:text-4xl">
              Manage wallet balance, subscription checkout, and payment history from one screen.
            </h1>
            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-200 sm:text-base">
              This page keeps teacher payment actions focused: top up through Razorpay or UPI, complete subscription purchases, and audit recent payment movement.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/teacher/subscription-plans')}
                className="inline-flex items-center rounded-full bg-white px-5 py-3 text-sm font-semibold text-slate-950 transition hover:bg-slate-100"
              >
                Subscription plans
              </button>
              <button
                type="button"
                onClick={() => navigate('/teacher/dashboard')}
                className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-5 py-3 text-sm font-semibold text-white transition hover:bg-white/15"
              >
                Back to dashboard
              </button>
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-white/10 bg-white/10 p-4 backdrop-blur">
            <div className="rounded-[1.25rem] border border-white/10 bg-slate-950/30 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-300">Checkout status</p>
              <p className="mt-3 text-3xl font-semibold">{paymentState ? 'Ready to pay' : 'Wallet only'}</p>
              <p className="mt-2 text-sm text-slate-300">
                {paymentState ? `${paymentState.planName} is staged for checkout.` : 'Top up or review recent transactions.'}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="grid gap-4 md:grid-cols-3">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          return (
            <div key={metric.label} className="rounded-[1.75rem] border border-slate-200 bg-white p-5 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">{metric.label}</p>
                  <p className="mt-3 text-3xl font-semibold tracking-tight text-slate-950">{metric.value}</p>
                  <p className="mt-2 text-sm text-slate-500">{metric.hint}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                  <Icon className="h-6 w-6 text-slate-700" />
                </div>
              </div>
            </div>
          );
        })}
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <div className="space-y-6">
          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Wallet balance</p>
                <p className="mt-3 text-4xl font-semibold tracking-tight text-slate-950">{formatCurrency(walletBalance)}</p>
                <p className="mt-2 text-sm text-slate-500">Refresh this after top-ups or wallet-based subscription payments.</p>
              </div>
              <button
                type="button"
                onClick={loadWalletBalance}
                className="inline-flex items-center rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <ArrowPathIcon className="mr-2 h-4 w-4" />
                Refresh
              </button>
            </div>
          </div>

          <div className="rounded-[2rem] border border-slate-200 bg-slate-50 p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.2)]">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Add funds</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Top up wallet via Razorpay / UPI</h2>
            <p className="mt-2 text-sm text-slate-500">Fund your wallet first if you want enough balance for wallet checkout.</p>
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <input
                type="number"
                min="1"
                step="0.01"
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full rounded-[1.25rem] border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-slate-500 sm:max-w-[220px]"
              />
              <button
                type="button"
                onClick={continueWithRazorpay}
                className="rounded-[1.25rem] bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Add funds
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          {paymentState ? (
            <div className="rounded-[2rem] border border-sky-200 bg-[linear-gradient(135deg,_#f8fbff_0%,_#eef6ff_100%)] p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.24)]">
              <div className="flex items-start gap-3">
                <ShieldCheckIcon className="mt-0.5 h-6 w-6 text-sky-700" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">Subscription payment</p>
                  <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">{paymentState.planName}</h2>
                  <p className="mt-2 text-sm text-slate-600">
                    {paymentState.mode === 'upgrade' ? 'Upgrade' : 'New subscription'} • {paymentState.billingCycle}
                  </p>
                  <p className="mt-4 text-4xl font-semibold tracking-tight text-slate-950">
                    {formatCurrency(paymentState.amount, paymentState.currency)}
                  </p>
                  {previewLoading ? (
                    <p className="mt-2 text-sm text-slate-500">Calculating prorated switch amount...</p>
                  ) : paymentState.prorationPreview ? (
                    <p className="mt-2 text-sm text-slate-600">
                      Reduced from {formatCurrency(Number(paymentState.originalAmount || 0), paymentState.currency)} with {formatCurrency(Number(paymentState.prorationPreview.currentPlanUnusedCredit || 0), paymentState.currency)} credit for {paymentState.prorationPreview.remainingDays} remaining days.
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  disabled={loading || previewLoading}
                  onClick={subscribeWithWallet}
                  className="rounded-[1.25rem] bg-emerald-600 px-5 py-4 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                >
                  {loading ? 'Processing...' : 'Pay with wallet'}
                </button>
                <button
                  type="button"
                  disabled={loading || previewLoading || paymentState.amount <= 0}
                  onClick={continueWithRazorpay}
                  className="rounded-[1.25rem] border border-slate-200 bg-white px-5 py-4 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60"
                >
                  Pay via Razorpay / UPI
                </button>
              </div>
            </div>
          ) : (
            <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.24)]">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Subscription checkout</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">No pending plan selected</h2>
              <p className="mt-2 text-sm text-slate-500">Choose a teacher subscription plan first, then return here to complete payment.</p>
              <button
                type="button"
                onClick={() => navigate('/teacher/subscription-plans')}
                className="mt-5 rounded-full bg-slate-950 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                View subscription plans
              </button>
            </div>
          )}

          <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.35)]">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Recent payments</p>
                <h2 className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">Subscription payment history</h2>
              </div>
              <button
                type="button"
                onClick={loadPaymentHistory}
                className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                Refresh history
              </button>
            </div>

            {historyLoading ? (
              <p className="mt-6 text-sm text-slate-500">Loading payment history...</p>
            ) : history.length === 0 ? (
              <p className="mt-6 text-sm text-slate-500">No payment history found.</p>
            ) : (
              <div className="mt-6 space-y-3">
                {history.slice(0, 8).map((item) => (
                  <div key={item.id} className="flex flex-col gap-3 rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-semibold text-slate-950">
                        {new Date(item.date).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                      <p className="mt-1 text-xs text-slate-500">Recorded subscription payment event</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <p className="text-sm font-semibold text-slate-950">{formatCurrency(Number(item.amount || 0))}</p>
                      <span className={`rounded-full border px-3 py-1 text-xs font-semibold capitalize ${statusClasses[item.status] || 'border-slate-200 bg-slate-100 text-slate-700'}`}>
                        {item.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 rounded-[1.25rem] border border-slate-200 bg-slate-50 px-4 py-4">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Total successful payments</p>
              <p className="mt-2 text-2xl font-semibold tracking-tight text-slate-950">{formatCurrency(totalPaid)}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TeacherPayments;
